import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { flexRender, Row } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';

import { TableCell, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';

import { cn } from '@/lib/utils';
import type { DataTableRow as DataRow, TableAction } from '../types';

import { RowActions } from './row-actions';

interface DataTableRowProps<T extends DataRow> {
  row: Row<T>;
  selectable?: boolean;
  actions?: TableAction<T>[];
  hierarchical?: boolean;
  level?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  renderExpandIcon?: (row: T, isExpanded: boolean) => ReactNode;
  rowClassName?: (row: T) => string;
  hasChildren?: boolean;
}

interface ColumnWithMeta {
  hideOnLevel?: number[];
  className?: string | ((row: unknown) => string);
  accessorKey?: string;
}

export const DataTableRow = function DataTableRow<T extends DataRow>({
  row,
  selectable = false,
  actions = [],
  hierarchical = false,
  level = 0,
  isExpanded = false,
  onToggleExpand,
  renderExpandIcon,
  rowClassName,
  hasChildren = false
}: DataTableRowProps<T>) {
  const { t } = useTranslation();
  const rowData = row.original;

  const defaultRowClass = cn('hover:bg-muted/50', rowClassName?.(rowData));

  return (
    <TableRow className={defaultRowClass}>
      {selectable && (
        <TableCell>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => {
              row.toggleSelected(!!value);
            }}
            aria-label={t('common.table.selectRow', { id: row.id, defaultValue: `Select row ${row.id}` })}
          />
        </TableCell>
      )}
      {row.getVisibleCells().map((cell, cellIndex) => {
        const column = cell.column.columnDef;
        const columnMeta = column as unknown as ColumnWithMeta;
        const width = (column.meta as { width?: string })?.width;

        // Check if column should be hidden at this level
        if (columnMeta.hideOnLevel && level !== undefined && columnMeta.hideOnLevel.includes(level)) {
          return <TableCell key={cell.id} />;
        }

        const cellContent = column.cell ? flexRender(column.cell, cell.getContext()) : (cell.getValue() as ReactNode);

        const isFirstVisibleCell = cellIndex === 0;
        const shouldShowExpandIcon = hierarchical && isFirstVisibleCell && hasChildren && onToggleExpand;

        return (
          <TableCell
            key={cell.id}
            className={cn(
              width || '',
              typeof columnMeta.className === 'function' ? columnMeta.className(rowData) : columnMeta.className
            )}>
            {shouldShowExpandIcon ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onToggleExpand();
                    }
                  }}>
                  {renderExpandIcon ? (
                    renderExpandIcon(rowData, isExpanded)
                  ) : (
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform duration-200',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  )}
                </button>
                <span>{cellContent}</span>
              </div>
            ) : (
              cellContent
            )}
          </TableCell>
        );
      })}
      {actions.length > 0 && (
        <TableCell>
          <RowActions actions={actions} row={row.original} />
        </TableCell>
      )}
    </TableRow>
  );
};
